import { UserSelect } from "@jasper/forms";
import { Box, Card, Space, Tabs, Text, Title } from "@mantine/core";
import { IconInfoCircle, IconUserCog } from "@tabler/icons-react";
import { useState } from "react";
import {
  AccountProductPreferences,
  useAccountProductPreferences,
} from "./hooks/useAccountProductPreferences";
import DeliveryPreferences from "./views/delivery/DeliveryPreferences";
import FixedPreferences from "./views/fixed/FixedPreferences";
import GeneralPreference from "./views/general/GeneralPreference";
import ImplantPreferences from "./views/implant/ImplantPreferences";
import RemovablePreferences from "./views/removable/RemovablePreferences";
import { useAuthContext } from "/@/shared/contexts/AuthContext";

const PreferencesPage = () => {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedPreferences, setSelectedPreferences] = useState<
    AccountProductPreferences | undefined
  >(undefined);

  const { user } = useAuthContext();

  const getDentistProductPreferences = useAccountProductPreferences();

  // Preferences are resolved before the dentist is selected, so the tab forms
  // mount with their real default values (cards snapshot them as "saved")
  const onChange = async (dentistId: string | null) => {
    const dentistPreferences = dentistId
      ? await getDentistProductPreferences(dentistId)
      : undefined;
    setSelectedPreferences(dentistPreferences);
    setSelectedUser(dentistId);
  };

  return (
    <Box m="s12">
      <Title order={2} variant="display">
        Préférences
      </Title>
      <Text
        variant="small"
        ff="Archivo"
        mb="s6"
        style={{
          color: "var(--mantine-color-ink-4)",
        }}
      >
        Vos réglages par défaut · appliqués à chaque nouvelle commande,
        modifiables dans le flux
      </Text>
      {user?.userGroup?.id && (
        <Card>
          <UserSelect
            mb="s2"
            placeholder="Sélectionnez un dentiste"
            label={
              <Box display="flex" style={{ alignItems: "center" }} mb="s1">
                <IconUserCog size={25} />
                &nbsp;
                <Text variant="body" size="xs" tt="uppercase" fw="500" ml="s2">
                  Préférences de
                </Text>
              </Box>
            }
            data={(user?.userGroup?.groupMembers ?? []).map(member => ({
              id: member.id,
              label: `Dr. ${member.firstName} ${member.lastName}`,
              firstName: member.firstName,
              lastName: member.lastName,
            }))}
            onChange={onChange}
          />
          <Text
            variant="caption"
            display="flex"
            mt="s2"
            style={{ alignItems: "center" }}
          >
            <IconInfoCircle />
            &nbsp;
            {
              "Vous modifiez les préférences de votre compte. Un titulaire peut régler celles d'un associé."
            }
          </Text>
        </Card>
      )}
      <Space h="xl" />
      {selectedUser && (
        <Tabs variant="sidebar" orientation="vertical" defaultValue="general">
          <Tabs.List>
            <Tabs.Tab value="general">Général</Tabs.Tab>
            <Tabs.Tab value="fixed">Restauration fixe</Tabs.Tab>
            <Tabs.Tab value="implant">Sur implant</Tabs.Tab>
            <Tabs.Tab value="removable">Amovible</Tabs.Tab>
            <Tabs.Tab value="delivery">Cabinet & livraison</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="general">
            <GeneralPreference
              key={selectedUser}
              selectedPreferences={selectedPreferences}
              selectedUser={selectedUser}
            />
          </Tabs.Panel>
          <Tabs.Panel value="fixed">
            <FixedPreferences />
          </Tabs.Panel>
          <Tabs.Panel value="implant">
            <ImplantPreferences />
          </Tabs.Panel>
          <Tabs.Panel value="removable">
            <RemovablePreferences />
          </Tabs.Panel>
          <Tabs.Panel value="delivery">
            <DeliveryPreferences />
          </Tabs.Panel>
        </Tabs>
      )}
    </Box>
  );
};

export default PreferencesPage;
